import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api, { apiUrl } from '../lib/api.js';

// Import de justificatifs en masse : upload d'un PDF multi-pages, suivi du traitement,
// confirmation des rapprochements, ZIP des non-rapprochés.

export function useUploadReceiptBatch() {
  return useMutation({
    mutationFn: (file) => {
      const fd = new FormData();
      fd.append('file', file);
      return api.upload('/receipt-batches', fd);
    },
  });
}

export function useReceiptBatch(batchId) {
  return useQuery({
    queryKey: ['receipt-batch', batchId],
    queryFn: () => api.get(`/receipt-batches/${batchId}`),
    enabled: Boolean(batchId),
    // Poll tant que le traitement asynchrone est en cours.
    refetchInterval: (query) =>
      query.state.data?.batch?.status === 'processing' ? 1500 : false,
  });
}

export function receiptPreviewUrl(batchId, itemId) {
  return apiUrl(`/receipt-batches/${batchId}/items/${itemId}/preview`);
}

export function unmatchedZipUrl(batchId) {
  return apiUrl(`/receipt-batches/${batchId}/unmatched.zip`);
}

export function useConfirmReceiptItem(batchId) {
  const qc = useQueryClient();
  return useMutation({
    // category_id omis (pas null) quand rien n'est choisi — le schéma Fastify du body
    // est en additionalProperties:false avec un uuid strict. La TVA est transmise
    // explicitement (null = effacée volontairement) : elle prime sur l'extraction.
    // vat_breakdown (multi-taux) n'est envoyé que s'il est fourni et complet.
    mutationFn: ({ itemId, transaction_id, entity_id, category_id, vat_rate, vat_amount_cents, vat_breakdown }) =>
      api.post(`/receipt-batches/${batchId}/items/${itemId}/confirm`, {
        transaction_id,
        entity_id,
        ...(category_id ? { category_id } : {}),
        ...(vat_rate !== undefined || vat_amount_cents !== undefined
          ? { vat_rate: vat_rate ?? null, vat_amount_cents: vat_amount_cents ?? null }
          : {}),
        ...(vat_breakdown ? { vat_breakdown } : {}),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['receipt-batch', batchId] });
      qc.invalidateQueries({ queryKey: ['transactions'] });
      // La TVA/catégorie appliquées changent la cellule « à catégoriser » et les
      // agrégats du cash-flow — sans cette invalidation la cellule reste périmée.
      qc.invalidateQueries({ queryKey: ['cash-flow'] });
    },
  });
}

export function useUnmatchReceiptItem(batchId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (itemId) => api.post(`/receipt-batches/${batchId}/items/${itemId}/unmatch`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['receipt-batch', batchId] });
    },
  });
}

export function useCancelReceiptUpload(batchId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (itemId) => api.post(`/receipt-batches/${batchId}/items/${itemId}/cancel-upload`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['receipt-batch', batchId] });
      qc.invalidateQueries({ queryKey: ['transactions'] });
      qc.invalidateQueries({ queryKey: ['cash-flow'] });
    },
  });
}

export function useUploadAllMatched(batchId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post(`/receipt-batches/${batchId}/upload-all`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['receipt-batch', batchId] });
      qc.invalidateQueries({ queryKey: ['transactions'] });
      qc.invalidateQueries({ queryKey: ['cash-flow'] });
    },
  });
}
